/**
 * RINF (era:) values in the facts: the helper nodes RINF uses for one parameter (pantographs,
 * contact line system, validity, ...) read as one line, and small pictures for the parameters a
 * shape does not draw yet (gauging profile, contact strip, contact line, traffic, TEN level).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeEntity } from '@services/sparql.service';
import { getIntlLocale } from '@utils/locale.utils';
import type { EntityObject, EntityProperty } from '@/types';
import { ERA } from '../page';
import { formatKm, localName, words } from '../linearLocation';
import { trackKm } from './line';
import { AnyValue, formatDate, UriLink, uriText } from './values';
import type { WidgetContext } from './types';
import styles from './widgets.module.scss';

const TIME = 'http://www.w3.org/2006/time#';
const DATA = 'https://data.banenor.no/data/';
const COUNTRY = 'http://publications.europa.eu/resource/authority/country/';

/** ISO 3166 alpha-3 to alpha-2 for the countries a Norwegian network meets. */
const ALPHA2: Record<string, string> = { NOR: 'NO', SWE: 'SE', FIN: 'FI', DNK: 'DK', DEU: 'DE' };

const described = new Map<string, Promise<EntityProperty[]>>();

export function useDescribed(iris: string[]): Record<string, EntityProperty[]> {
  const [nodes, setNodes] = useState<Record<string, EntityProperty[]>>({});
  const key = iris.join(' ');
  useEffect(() => {
    let cancelled = false;
    const list = key ? key.split(' ') : [];
    Promise.all(
      list.map((iri) => {
        if (!described.has(iri)) described.set(iri, describeEntity(iri).catch(() => []));
        return described.get(iri)!.then((properties) => [iri, properties] as const);
      })
    ).then((entries) => !cancelled && setNodes(Object.fromEntries(entries)));
    return () => {
      cancelled = true;
    };
  }, [key]);
  return nodes;
}

const first = (properties: EntityProperty[] | undefined, predicate: string) =>
  properties?.find((property) => property.predicate === predicate)?.objects[0];

/** A date from a time instant IRI such as _timeinstant_2019-01-01. */
const instantDate = (iri: string | undefined) => iri?.match(/(\d{4}-\d{2}-\d{2})/)?.[1];

function NodeSummary({ iri, node, context }: { iri: string; node: EntityProperty[] | undefined; context: WidgetContext }): JSX.Element {
  const { t, i18n } = useTranslation();
  const date = (value: string) => formatDate(value, i18n.language);
  const text = (object: EntityObject | undefined) => (object ? (object.type === 'uri' ? uriText(object.value, context) : object.value) : '');
  const name = localName(iri);

  if (name.startsWith('_linearlocation')) {
    const km = trackKm(context);
    if (km) return <span>km {formatKm(km[0])}–{formatKm(km[1])}</span>;
  }
  if (!node) return <span className={styles.symbolContext}>…</span>;
  const types = node.find((property) => property.predicate.endsWith('#type'))?.objects.map((object) => object.value) ?? [];

  if (types.includes(`${ERA}RaisedPantographsDistanceAndSpeed`)) {
    return (
      <span>
        {t('entity.template.rinf.pantographs', {
          count: Number(first(node, `${ERA}raisedPantographsNumber`)?.value ?? 0),
          distance: first(node, `${ERA}raisedPantographsDistance`)?.value ?? '–',
          speed: first(node, `${ERA}raisedPantographsSpeed`)?.value ?? '–',
        })}
      </span>
    );
  }
  if (types.includes(`${ERA}ContactLineSystem`)) {
    const min = first(node, `${ERA}minimumContactWireHeight`)?.value;
    const max = first(node, `${ERA}maximumContactWireHeight`)?.value;
    return (
      <span className={styles.rinfValue}>
        <OverheadLine />
        <span>
          {[text(first(node, `${ERA}energySupplySystem`)), text(first(node, `${ERA}contactLineSystemType`))].filter(Boolean).join(' · ')}
          {min && max && ` · ${t('entity.template.rinf.wire', { min: Number(min), max: Number(max) })}`}
        </span>
      </span>
    );
  }
  if (types.includes(`${TIME}Interval`) || types.includes(`${ERA}TemporalFeature`)) {
    const from = instantDate(first(node, `${TIME}hasBeginning`)?.value);
    const to = instantDate(first(node, `${TIME}hasEnd`)?.value);
    return <span>{from ? `${date(from)} → ${to ? date(to) : t('entity.template.about.open')}` : '–'}</span>;
  }
  if (types.includes(`${ERA}LinesideDistanceIndication`)) {
    return (
      <span>
        {[text(first(node, `${ERA}linesideDistanceIndicationAppearance`)), first(node, `${ERA}linesideDistanceIndicationFrequency`) && t('entity.template.rinf.every', { metres: first(node, `${ERA}linesideDistanceIndicationFrequency`)!.value })]
          .filter(Boolean)
          .join(' · ')}
      </span>
    );
  }
  const literals = node.filter((property) => property.objects[0]?.type === 'literal').slice(0, 3);
  return (
    <span>
      {literals.length > 0
        ? literals.map((property) => `${words(localName(property.predicate)).toLowerCase()} ${property.objects[0].value}`).join(' · ')
        : uriText(iri, context)}
    </span>
  );
}

function OverheadLine(): JSX.Element {
  return (
    <svg className={styles.picture} width="40" height="20" viewBox="0 0 40 20" aria-hidden="true">
      <path d="M2 5 H38" className={styles.pictureStroke} />
      <path d="M8 2 V18 M32 2 V18" className={styles.pictureBase} />
      <path d="M14 12 L20 5 L26 12 M12 12 H28" className={styles.pictureMuted} />
    </svg>
  );
}

/** The wider and taller loading gauges (GC, G2, DE3, NO1), by their ERA concept. */
const BIG_GAUGES = ['gaugings/30', 'gaugings/60', 'gaugings/50', 'gaugings/eratv/n01'].map((code) => `${ERA}concepts/${code}`);

function GaugeOutline({ concept }: { concept: string }): JSX.Element {
  // A loading gauge drawn as a vehicle outline; the wider and taller profiles get more room.
  const big = BIG_GAUGES.includes(concept);
  return (
    <svg className={styles.picture} width="20" height="22" viewBox="0 0 20 22" aria-hidden="true">
      <path d={big ? 'M3 20 V6 Q3 2 10 2 Q17 2 17 6 V20' : 'M5 20 V8 Q5 5 10 5 Q15 5 15 8 V20'} className={styles.pictureStroke} />
      <path d="M1 21 H19" className={styles.pictureBase} />
    </svg>
  );
}

function Traffic({ label }: { label: string }): JSX.Element {
  const freight = /freight|gods/i.test(label);
  return (
    <svg className={styles.picture} width="26" height="16" viewBox="0 0 26 16" aria-hidden="true">
      {freight ? (
        <path d="M2 4 H24 V12 H2 Z M6 14 a1.5 1.5 0 1 0 0.1 0 M20 14 a1.5 1.5 0 1 0 0.1 0" className={styles.pictureStroke} />
      ) : (
        <path d="M3 12 V5 Q3 3 6 3 H22 Q24 3 24 6 V12 Z M7 6 H11 M14 6 H18" className={styles.pictureStroke} />
      )}
    </svg>
  );
}

/** An overhead contact line: a mast with its cantilever, the catenary and the contact wire under it. */
const Catenary = (): JSX.Element => (
  <svg className={styles.catenaryPicture} width="30" height="20" viewBox="0 0 30 20" aria-hidden="true">
    <path d="M4 19V2M4 4h9M2 6c7 3 19 3 26 0M2 10h26M9 7v3M16 8v2M23 7v3" />
  </svg>
);

const ROW_PICTURE: Record<string, 'gauge' | 'traffic' | 'strip' | 'ten' | 'catenary'> = {
  [`${ERA}contactLineSystem`]: 'catenary',
  [`${ERA}gaugingProfile`]: 'gauge',
  [`${ERA}typeOfTraffic`]: 'traffic',
  [`${ERA}contactStripMaterial`]: 'strip',
  [`${ERA}tentNetworkLevel`]: 'ten',
};

/**
 * RINF's gradient profile ("-16.8(+18.428), -15.8(+18.484), ...": per mille from a km) as a small
 * bar profile with its range; the raw list folds out under it.
 */
function GradientProfile({ values, pictures }: { values: EntityObject[]; pictures: boolean }): JSX.Element | null {
  const { t } = useTranslation();
  const raw = values.flatMap((value) => value.value.split(/,\s*/)).filter(Boolean);
  const steps = raw
    .map((text) => text.match(/^\s*([-+]?\d+(?:\.\d+)?)\s*\(\+?([-\d.]+)\)/))
    .filter((match): match is RegExpMatchArray => match !== null)
    .map((match) => ({ permille: Number(match[1]), km: Number(match[2]) }))
    .sort((a, b) => a.km - b.km);
  if (steps.length === 0) return null;
  const km = trackKmFromSteps(steps);
  const max = Math.max(...steps.map((step) => Math.abs(step.permille)), 1);
  const W = 160;
  const H = 28;
  const x = (at: number) => ((at - km[0]) / (km[1] - km[0] || 1)) * W;
  const permilles = steps.map((step) => step.permille);
  return (
    <span className={styles.gradientProfile}>
      {pictures && (
        <svg className={styles.picture} width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
          <path d={`M0 ${H / 2}H${W}`} className={styles.pictureBase} />
          {steps.map((step, index) => {
            const end = steps[index + 1]?.km ?? km[1];
            const h = Math.max(1.5, (Math.abs(step.permille) / max) * (H / 2 - 2));
            return (
              <rect
                key={index}
                x={x(step.km) + 0.5}
                y={step.permille >= 0 ? H / 2 - h : H / 2}
                width={Math.max(1, x(end) - x(step.km) - 1)}
                height={h}
                className={styles.gradientBar}
              />
            );
          })}
        </svg>
      )}
      <span>
        {t('entity.template.rinf.gradients', { count: steps.length, min: Math.min(...permilles), max: Math.max(...permilles) })}
      </span>
      <details className={styles.rawList}>
        <summary>{t('entity.template.rinf.showList')}</summary>
        {raw.join(', ')}
      </details>
    </span>
  );
}

/** The km range of a gradient profile: from its first step to a little past its last. */
function trackKmFromSteps(steps: { km: number }[]): [number, number] {
  const first = steps[0].km;
  const last = steps[steps.length - 1].km;
  const average = steps.length > 1 ? (last - first) / (steps.length - 1) : 0.1;
  return [first, last + average];
}

/** The values of one era: predicate, drawn and summarised. */
export function RinfValues({ predicate, values, context, limit }: { predicate: string; values: EntityObject[]; context: WidgetContext; limit: number }): JSX.Element {
  const { i18n } = useTranslation();
  const nodes = useDescribed(
    values.filter((value) => value.type === 'uri' && value.value.startsWith(DATA) && !localName(value.value).startsWith('_linearlocation')).map((value) => value.value).slice(0, 12)
  );
  const picture = ROW_PICTURE[predicate];
  if (predicate === `${ERA}gradientProfile`) {
    const profile = <GradientProfile values={values} pictures={context.pictures ?? true} />;
    if (values.some((value) => /\(\+?[-\d.]+\)/.test(value.value))) return profile;
  }
  return (
    <span className={styles.rinfValues}>
      {values.slice(0, limit).map((value) => {
        if (value.type === 'uri' && value.value.startsWith(COUNTRY)) {
          const code = ALPHA2[localName(value.value)];
          const name = code ? new Intl.DisplayNames([getIntlLocale(i18n.language)], { type: 'region' }).of(code) : localName(value.value);
          return <span key={value.value}>{name}</span>;
        }
        if (value.type === 'uri' && value.value.startsWith(DATA) && !context.resolved[value.value]) {
          return (
            <span key={value.value} className={styles.rinfValue}>
              <UriLink uri={value.value} context={context}>
                <NodeSummary iri={value.value} node={nodes[value.value]} context={context} />
              </UriLink>
            </span>
          );
        }
        const label = value.type === 'uri' ? uriText(value.value, context) : value.value;
        return (
          <span key={value.value} className={styles.rinfValue}>
            {context.pictures && picture === 'gauge' && <GaugeOutline concept={value.value} />}
            {context.pictures && picture === 'traffic' && <Traffic label={label} />}
            {context.pictures && picture === 'strip' && <span className={styles.stripPicture} aria-hidden="true" />}
            {context.pictures && picture === 'catenary' && <Catenary />}
            {picture === 'ten' && <span className={styles.tenBadge}>TEN-T</span>}
            {value.type === 'uri' && label.startsWith('era:') ? (
              <UriLink uri={value.value} context={context}>
                {label.startsWith('era:concepts/') ? label.replace(/^era:concepts\//, '') : words(localName(value.value)).toLowerCase()}
              </UriLink>
            ) : (
              <AnyValue value={value} context={context} />
            )}
          </span>
        );
      })}
    </span>
  );
}
